import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSiteId, setCurrentSiteId] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('netzero_user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // Default existing users to approved so current flows keep working
      if (!parsed.approvalStatus) {
        parsed.approvalStatus = 'approved';
      }
      setUser(parsed);
    }
    setLoading(false);
  }, []);

  const persistUser = (userObj) => {
    setUser(userObj);
    localStorage.setItem('netzero_user', JSON.stringify(userObj));
  };

  const login = async (email, password) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';

    const res = await fetch(`${baseUrl}/api/user/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, provider: 'Direct' }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Login failed');
    }

    const body = await res.json();

    // Backend returns { success, user, organisation }
    if (!body.success || !body.user) {
      throw new Error(body.message || 'Login failed');
    }

    const apiUser = body.user;

    const userFromApi = {
      id: apiUser.id,
      email: apiUser.email,
      name: apiUser.fullName || apiUser.email || '',
      jobTitle: apiUser.jobTitle || '',
      role: apiUser.role || 'User',
      organisationId: apiUser.organisationId || null,
      organisation: body.organisation || null,
      approvalStatus: apiUser.approvalStatus || 'approved',
    };

    persistUser(userFromApi);
    return { success: true, user: userFromApi };
  };

  const signup = async (userData) => {
    // Mock signup - replace with actual API call
    // At this point the user has created credentials but has not yet submitted
    // their organisation details for approval.
    const mockUser = {
      id: '1',
      email: userData.email,
      name: userData.fullName || userData.name || '',
      approvalStatus: 'unapplied',
    };
    persistUser(mockUser);
    return { success: true, user: mockUser };
  };

  const submitApprovalApplication = async (applicationData) => {
    // This is where you'll later POST to your Mongo-backed API.
    // For now we just store the data locally and mark the user as pending.
    if (!user) {
      throw new Error('No authenticated user to attach approval application to');
    }

    const updatedUser = {
      ...user,
      approvalStatus: 'pending',
      approvalApplication: {
        ...applicationData,
        submittedAt: new Date().toISOString(),
      },
    };

    // Simulate a small network delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    persistUser(updatedUser);
    return { success: true, user: updatedUser };
  };

  const logout = () => {
    setUser(null);
    setCurrentSiteId(null);
    localStorage.removeItem('netzero_user');
  };

  const value = {
    user,
    currentSiteId,
    setCurrentSiteId,
    login,
    signup,
    submitApprovalApplication,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};